import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Pressable, TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Edges, SafeAreaView } from 'react-native-safe-area-context';
import { useCameraPermission } from 'react-native-vision-camera';
import { useDispatch, useSelector } from 'react-redux';

import { useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { AppLogo } from 'assets/images';
import QRCode from 'assets/images/Login/ico_qr_code.svg';

import { getAccountInfo } from 'config/api.ts';
import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    FORGOT_PASSWORD_BUTTON,
    LOGIN_BUTTON,
    LOGIN_FAILED_DESCRIPTION,
    LOGIN_FAILED_TITLE,
    LOGIN_PASSWORD,
    LOGIN_PASSWORD_EMPTY_MESSAGE,
    LOGIN_USERNAME,
    LOGIN_USERNAME_EMPTY_MESSAGE,
    LOGO_TEST_ID,
    TRY_AGAIN_BUTTON_ID,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { getBookmarks } from 'store/bookmarks/actions';
import { selectLayoutConfig } from 'store/config/selectors';
import { getMainMenuConfig } from 'store/railsBuilder/actions';
import {
    accountInfoSuccess,
    getPinsStatus,
    loginFailure,
    loginWithCredentials,
} from 'store/user/actions';
import {
    getHaveToShowPinSetup,
    getPinsStatusRequestStatus,
    selectIsNonInteractiveUI,
    selectLoginError,
} from 'store/user/selectors';
import { requestFailure } from 'store/utils';

import Announcement from 'services/accessibility/Announcement';
import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import NagraInstance from 'utils/axios/nagra/DefaultInstance.ts';
import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import useNoInternet from 'utils/hooks/useNoInternet';
import redirectToTerms from 'utils/redirectToTerms';
import {
    isPhone,
    isTablet,
    isTabletOrWeb,
    isWeb,
    widthPercentageToDP,
} from 'utils/responsiveness';

import ForgotPasswordDialog from 'screens/Login/ForgotPasswordDialog';

import CredentialInput from 'components/CredentialInput';
import ActionButton from 'components/CustomButtons/ActionButton';
import ErrorDialog from 'components/ErrorDialog';
import LocalizedText from 'components/LocalizedText';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';
import { validatePassword, validateUsername } from './utils';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams>;
};

const {
    CLOSE_BUTTON,
    TRY_AGAIN_BUTTON,
    LOGIN: {
        SCREEN_LOGIN_PRIVACY_POLICY_HINT,
        SCREEN_LOGIN_PRIVACY_POLICY_LINK,
        SCREEN_LOGIN_BUTTON,
        SCREEN_LOGIN_FORGOT,
        SCREEN_LOGIN_PASSWORD,
        SCREEN_LOGIN_PASSWORD_FIELD,
        SCREEN_LOGIN_USERNAME_FIELD,
        SCREEN_LOGIN_ALERT_LOGIN_FAILED,
        SCREEN_LOGIN_PRIVACY_POLICY_HINT_TEXT,
        SCREEN_LOGIN_PRIVACY_POLICY_LINK_BUTTON,
        SCREEN_LOGIN_PRIVACY_POLICY_LINK_TEXT,
        LOGIN_SCAN_QR_CODE_BUTTON,
    },
    ACCESSIBILITY: {
        LOGIN: {
            ACCESSIBILITY_LOGIN_PRIVACY_POLICY_TERMS,
            ACCESSIBILITY_LOGIN_USERNAME_INPUT,
            ACCESSIBILITY_LOGIN_PASSWORD_INPUT,
            ACCESSIBILITY_LOGIN_FORGOT_PASSWORD,
            ACCESSIBILITY_LOGIN_LOGIN_BUTTON,
            ACCESSIBILITY_LOGIN_LOGIN_BUTTON_WEB,
        },
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
    },
} = LANGUAGE_KEYS;
const edges: Edges = ['top'];

const LoginScreen = ({ navigation }: Props) => {
    const usernameRef = useRef<typeof TouchableOpacity>(null);
    const passwordRef = useRef<typeof TouchableOpacity>(null);
    const accessibleViewRef = useRef(null);

    // Variables and Const
    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();
    const dispatch = useDispatch();
    const { isInternetConnected } = useNoInternet();
    const { t } = useTranslation();
    const { hasPermission, requestPermission } = useCameraPermission();

    const {
        background,
        primary,
        error_color: errorColor,
    } = useSelector(selectLayoutConfig);
    const loginError = useSelector(selectLoginError);
    const haveToShowPinSetup = useSelector(getHaveToShowPinSetup);
    const pinsStatusRequestStatus = useSelector(getPinsStatusRequestStatus);
    const isNonInteractive = useSelector(selectIsNonInteractiveUI);

    const [username, setUsername] = useState('');
    const [usernameErrorText, setUsernameErrorText] = useState('');
    const [password, setPassword] = useState('');
    const [passwordErrorText, setPasswordErrorText] = useState('');
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showForgotPasswordDialog, setShowForgotPasswordDialog] =
        useState<boolean>(false);
    const [focused, setFocused] = useState(false);
    const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);

    const wasLoginClicked = useRef(false);

    const appLogoSize = widthPercentageToDP(64, 80, 80);

    const isPinRequestFailed = useMemo(
        () => requestFailure(pinsStatusRequestStatus),
        [pinsStatusRequestStatus],
    );

    const { onEnterTheApp } = useEnterTheApp();

    const handlePinStatusCheckSuccess = useCallback(async () => {
        const tutorialPageShown = await RegularStorage.getItem(
            StorageKeys.TUTORIAL_PAGE_SHOWN,
        );

        if (haveToShowPinSetup) {
            navigation.replace(SCREEN.PIN_SETUP);
        } else if (tutorialPageShown || isNonInteractive) {
            await onEnterTheApp();
        } else {
            navigation.replace(SCREEN.FTI_TUTORIAL);
        }
    }, [navigation, haveToShowPinSetup, onEnterTheApp, isNonInteractive]);

    const handleGetPinStatus = useCallback(() => {
        const retryGetPinsStatusAction = () =>
            new Promise<void>((resolve, reject) => {
                dispatch(
                    getPinsStatus({
                        onSuccess: () => {
                            handlePinStatusCheckSuccess();
                            resolve();
                        },
                        onFailure: (error) => {
                            if (isErrorNeedsRetry(error)) {
                                reject(error);
                            } else {
                                dispatch(loginFailure(error));
                                wasLoginClicked.current = false;
                                resolve();
                            }
                        },
                    }),
                );
            });

        dispatch(
            getPinsStatus({
                onSuccess: handlePinStatusCheckSuccess,
                onFailure: (error) => {
                    if (isErrorNeedsRetry(error)) {
                        RetryController.showVitalDialog({
                            actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                            actionButtonOnPress: retryGetPinsStatusAction,
                        });
                        RetryController.registerRetryCallback(
                            retryGetPinsStatusAction,
                            {
                                retryDuration: 10 * 60 * 1000,
                                maxRetryCount: 10,
                            },
                        );
                        RetryController.executeRetry();
                        wasLoginClicked.current = false;
                    }
                },
            }),
        );
    }, [handlePinStatusCheckSuccess]);

    const handleCloseErrorModal = useCallback(() => {
        setShowErrorDialog(false);
    }, []);

    const loginPressed = useCallback(async () => {
        if (!wasLoginClicked.current) {
            wasLoginClicked.current = true;

            const usernameError = validateUsername(username);
            const passwordError = validatePassword(password);
            setUsernameErrorText(usernameError);
            setPasswordErrorText(passwordError);

            if (usernameError) {
                const announcement = new Announcement();

                await announcement.announce(t(usernameError), false);
                setAccessibilityFocusTo(usernameRef);
            }

            if (passwordError) {
                const announcement = new Announcement();

                await announcement.announce(t(passwordError), false);

                if (!usernameError) {
                    setAccessibilityFocusTo(passwordRef);
                }
            }

            if (usernameError === '' && passwordError === '') {
                dispatch(
                    loginWithCredentials(username, password, {
                        onSuccess: async () => {
                            const retryAccountInfoAction = async () => {
                                const {
                                    data: { sharedConsentPublicId },
                                } = await NagraInstance.get(getAccountInfo());

                                dispatch(
                                    accountInfoSuccess(sharedConsentPublicId),
                                );

                                dispatch(getMainMenuConfig());
                                dispatch(getBookmarks());
                                handleGetPinStatus();
                            };

                            try {
                                const {
                                    data: { sharedConsentPublicId },
                                } = await NagraInstance.get(getAccountInfo());

                                dispatch(
                                    accountInfoSuccess(sharedConsentPublicId),
                                );
                                dispatch(getMainMenuConfig());
                                dispatch(getBookmarks());
                                handleGetPinStatus();
                            } catch (error: any) {
                                if (isErrorNeedsRetry(error)) {
                                    RetryController.showVitalDialog({
                                        actionButtonTitle:
                                            LANGUAGE_KEYS.RELOAD_BUTTON,
                                        actionButtonOnPress:
                                            retryAccountInfoAction,
                                    });
                                    RetryController.registerRetryCallback(
                                        retryAccountInfoAction,
                                        {
                                            retryDuration: 10 * 60 * 1000,
                                            maxRetryCount: 10,
                                        },
                                    );
                                    RetryController.executeRetry();
                                    wasLoginClicked.current = false;
                                } else {
                                    dispatch(loginFailure(error));
                                    wasLoginClicked.current = false;
                                }
                            }
                        },
                        onFailure: () => {
                            wasLoginClicked.current = false;
                        },
                    }),
                );
            } else {
                wasLoginClicked.current = false;
            }
        }
    }, [username, password, t, handleGetPinStatus]);

    const handleQRLoginPress = useCallback(async () => {
        if (!hasPermission) {
            await requestPermission();
        }

        navigation.navigate(SCREEN.QR_LOGIN_SCREEN);
    }, [hasPermission]);

    const handleOpenForgotPasswordDialog = useCallback(() => {
        setShowForgotPasswordDialog(true);
    }, []);

    const handleCloseForgotPasswordDialog = useCallback(() => {
        setShowForgotPasswordDialog(false);
    }, []);

    const handleKeyUpPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter' && username && password) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                loginPressed();
            }
        },
        [username, password, loginPressed],
    );

    const renderErrorDescriptionComponent = useCallback(() => {
        if (loginError?.message) {
            return (
                <Text
                    category={ETextCategory.body1}
                    color={ETextColor.neutral}
                    style={styles.errorDescription}
                    center
                    testID={LOGIN_FAILED_DESCRIPTION}
                    accessible
                    accessibilityRole="text"
                    {...(isWeb && { tabIndex: 0 })}
                >
                    {t(loginError?.message)}
                </Text>
            );
        }
        return null;
    }, [t, loginError?.message]);

    const renderPrivacyContent = useCallback(
        () => (
            <View
                accessible={!isWeb}
                accessibilityLabel={
                    isWeb
                        ? undefined
                        : `${t(
                              ACCESSIBILITY_LOGIN_PRIVACY_POLICY_TERMS,
                          )} '.' ${t(ACCESSIBILITY_GENERAL_TAPP_TWICE)}`
                }
                onAccessibilityTap={isWeb ? undefined : redirectToTerms}
                style={[gStyles.flexRow, styles.privacyPolicyContent]}
            >
                <Text
                    category={ETextCategory.caption1}
                    color={ETextColor.neutral}
                    style={styles.privacyPolicyText}
                    testID={SCREEN_LOGIN_PRIVACY_POLICY_HINT_TEXT}
                    center
                >
                    {t(SCREEN_LOGIN_PRIVACY_POLICY_HINT)}{' '}
                </Text>
                <Pressable
                    importantForAccessibility="no"
                    onPress={redirectToTerms}
                    accessibilityLabel={
                        isWeb
                            ? `${t(
                                  ACCESSIBILITY_LOGIN_PRIVACY_POLICY_TERMS,
                              )} '.' ${t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB)}`
                            : undefined
                    }
                    testID={SCREEN_LOGIN_PRIVACY_POLICY_LINK_BUTTON}
                    accessibilityRole="link"
                    role="link"
                >
                    <Text
                        category={ETextCategory.caption1}
                        color={ETextColor.primary}
                        testID={SCREEN_LOGIN_PRIVACY_POLICY_LINK_TEXT}
                        style={styles.privacyPolicyText}
                        center
                        bold
                    >
                        {t(SCREEN_LOGIN_PRIVACY_POLICY_LINK)}
                    </Text>
                </Pressable>
            </View>
        ),
        [t],
    );

    useEffect(() => {
        if (loginError && focused) {
            setFailedLoginAttempts(
                (prevFailedLoginAttempts) => prevFailedLoginAttempts + 1,
            );
        }
    }, [loginError, focused]);

    useEffect(() => {
        if (failedLoginAttempts === 3) {
            handleOpenForgotPasswordDialog();
            setFailedLoginAttempts(0);
        } else if (failedLoginAttempts > 0) {
            setShowErrorDialog(true);
        }
    }, [failedLoginAttempts]);

    useFocusEffect(
        useCallback(() => {
            setFocused(true);

            return () => {
                setFocused(false);
            };
        }, []),
    );

    useEffect(
        () => () => {
            setFailedLoginAttempts(0);
        },
        [],
    );

    useEffect(() => {
        if (isWeb) {
            document.addEventListener('keyup', handleKeyUpPress);
        }

        return () => {
            if (isWeb) {
                document.removeEventListener('keyup', handleKeyUpPress);
            }
        };
    }, [handleKeyUpPress]);

    useEffect(() => {
        if (isWeb && focused) {
            setAccessibilityFocusTo(accessibleViewRef);
        }
    }, [focused]);

    return (
        <SafeAreaView
            style={[gStyles.container, { backgroundColor: background }]}
            edges={edges}
        >
            <KeyboardAwareScrollView
                alwaysBounceVertical={false}
                contentContainerStyle={gStyles.container}
                enableAutomaticScroll={false}
            >
                <View style={[gStyles.centerItems, gStyles.container]}>
                    <View
                        style={styles.topLogo}
                        ref={accessibleViewRef}
                        role="img"
                        accessibilityRole="image"
                        aria-label="OneTV logo"
                        {...(isWeb && { tabIndex: -1 })}
                        accessible
                    >
                        <AppLogo
                            testID={LOGO_TEST_ID}
                            width={appLogoSize}
                            height={appLogoSize}
                        />
                    </View>
                    <CredentialInput
                        ref={usernameRef}
                        accessible
                        accessibilityLabel={t(
                            ACCESSIBILITY_LOGIN_USERNAME_INPUT,
                        )}
                        id="username"
                        testID={LOGIN_USERNAME}
                        errotTextTestID={LOGIN_USERNAME_EMPTY_MESSAGE}
                        text={username}
                        placeholder={SCREEN_LOGIN_USERNAME_FIELD}
                        autoCorrect={false}
                        autoCapitalize="none"
                        autoComplete="off"
                        errorText={usernameErrorText}
                        errorColor={errorColor}
                        onEndEditing={() => {
                            usernameErrorText &&
                                setUsernameErrorText(
                                    validateUsername(username),
                                );
                        }}
                        onChangeText={(txt) => setUsername(txt)}
                    />

                    <CredentialInput
                        ref={passwordRef}
                        accessible
                        accessibilityLabel={t(
                            ACCESSIBILITY_LOGIN_PASSWORD_INPUT,
                        )}
                        id="password"
                        testID={LOGIN_PASSWORD}
                        errotTextTestID={LOGIN_PASSWORD_EMPTY_MESSAGE}
                        text={password}
                        placeholder={SCREEN_LOGIN_PASSWORD_FIELD}
                        autoCorrect={false}
                        autoCapitalize="none"
                        autoComplete="off"
                        errorText={passwordErrorText}
                        errorColor={errorColor}
                        onEndEditing={() => {
                            passwordErrorText &&
                                setPasswordErrorText(
                                    validatePassword(password),
                                );
                        }}
                        onChangeText={(txt) => setPassword(txt)}
                        secureTextEntry
                    />

                    <TouchableOpacity
                        testID={FORGOT_PASSWORD_BUTTON}
                        accessible
                        accessibilityLabel={t(
                            ACCESSIBILITY_LOGIN_FORGOT_PASSWORD,
                        )}
                        accessibilityRole="button"
                        style={styles.forgotPasswordView}
                        onPress={handleOpenForgotPasswordDialog}
                        // @ts-ignore
                        tabIndex={0}
                    >
                        <LocalizedText
                            textKey={[
                                SCREEN_LOGIN_FORGOT,
                                SCREEN_LOGIN_PASSWORD,
                            ]}
                            style={styles.forgotPassword}
                            numberOfLines={1}
                        />
                    </TouchableOpacity>

                    <View style={styles.bottomStyle}>
                        {isPhone && renderPrivacyContent()}
                        <ActionButton
                            accessible
                            accessibilityLabel={t(
                                isWeb
                                    ? ACCESSIBILITY_LOGIN_LOGIN_BUTTON_WEB
                                    : ACCESSIBILITY_LOGIN_LOGIN_BUTTON,
                            )}
                            testID={LOGIN_BUTTON}
                            title={SCREEN_LOGIN_BUTTON}
                            onPress={loginPressed}
                            fillColor={primary}
                            buttonTextStyle={{ color: background }}
                            buttonStyle={[
                                styles.button,
                                {
                                    width: widthPercentageToDP(328),
                                    marginBottom: isWeb
                                        ? Math.max(24, bottomInset)
                                        : 0,
                                },
                            ]}
                            withHint
                        />
                        {!isWeb && (
                            <ActionButton
                                accessible
                                accessibilityLabel={t(
                                    LOGIN_SCAN_QR_CODE_BUTTON,
                                )}
                                testID={LOGIN_BUTTON}
                                title={LOGIN_SCAN_QR_CODE_BUTTON}
                                onPress={handleQRLoginPress}
                                fillColor={background}
                                buttonTextStyle={styles.qrButtonText}
                                buttonStyle={[
                                    styles.qrButton,
                                    styles.button,
                                    {
                                        marginBottom: Math.max(24, bottomInset),
                                    },
                                ]}
                                Icon={QRCode}
                            />
                        )}
                    </View>

                    <View style={styles.bottomEmptySpace} />

                    {isTabletOrWeb && renderPrivacyContent()}
                    <ErrorDialog
                        visible={
                            isInternetConnected && showErrorDialog && focused
                        }
                        animationType="fade"
                        title={SCREEN_LOGIN_ALERT_LOGIN_FAILED}
                        titleTestID={LOGIN_FAILED_TITLE}
                        description={loginError?.message}
                        DescriptionComponent={renderErrorDescriptionComponent}
                        errorCode={loginError?.code}
                        actionButtonTestID={
                            loginError?.permanent
                                ? CLOSE_BUTTON
                                : TRY_AGAIN_BUTTON_ID
                        }
                        actionButtonTitle={
                            loginError?.permanent
                                ? CLOSE_BUTTON
                                : TRY_AGAIN_BUTTON
                        }
                        actionButtonFillColor={primary}
                        actionButtonTextStyle={styles.tryAgainButtonText}
                        actionButtonStyle={
                            isTablet &&
                            !loginError?.permanent &&
                            styles.tryAgainButtonTablet
                        }
                        actionButtonOnPress={
                            isPinRequestFailed
                                ? handleGetPinStatus
                                : handleCloseErrorModal
                        }
                        errorStatus={loginError?.status}
                    />

                    <ForgotPasswordDialog
                        visible={
                            isInternetConnected && showForgotPasswordDialog
                        }
                        closeDialog={handleCloseForgotPasswordDialog}
                    />
                </View>
            </KeyboardAwareScrollView>
        </SafeAreaView>
    );
};

export default LoginScreen;
